import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { api } from './api.js';
import { AsyncState, Button, Screen } from './ui.js';
import { useSession } from './session.js';

export interface LegalManifest {
  version: string;
  publishedAt: string | null;
  summary: string;
  documents: Array<{ type: string; title: string; version: string; hash: string; url: string }>;
}
export function acceptancePayload(manifest: LegalManifest, idempotencyKey?: string) {
  const terms = manifest.documents.find((doc) => doc.type === 'terms');
  const privacy = manifest.documents.find((doc) => doc.type === 'privacy');
  if (
    !terms ||
    !privacy ||
    privacy.version !== manifest.version ||
    !['v1', 'v2', 'v3'].includes(terms.version) ||
    !/^[a-f0-9]{64}$/.test(terms.hash) ||
    !/^[a-f0-9]{64}$/.test(privacy.hash)
  )
    throw new Error('Documents unavailable');
  return {
    terms: terms.version,
    privacy: privacy.version,
    hashes: { terms: terms.hash, privacy: privacy.hash },
    ...(idempotencyKey ? { idempotencyKey } : {}),
  };
}
export function useLegalManifest() {
  return useQuery({
    queryKey: ['legal-manifest'],
    queryFn: () => api<LegalManifest>('/legal/documents?type=manifest'),
    staleTime: 0,
  });
}
export function LegalRenewal({ manifest }: { manifest: LegalManifest }) {
  const client = useQueryClient();
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const accept = useMutation({
    mutationFn: () =>
      api('/me/onboarding/accept-legal', {
        method: 'POST',
        body: JSON.stringify(acceptancePayload(manifest, idempotencyKey)),
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['legal-status'] }),
    onError: () => {
      void client.invalidateQueries({ queryKey: ['legal-status'] });
    },
  });
  return (
    <Screen title="Обновлены документы PONCHIK" eyebrow={`Версия ${manifest.version}`}>
      <p>{manifest.summary}</p>
      <p>
        Для продолжения ознакомьтесь с новой редакцией. Старые согласия не заменяются автоматически.
        Этот выбор не включает AI и специальные 18+ функции.
      </p>
      {manifest.documents.map((doc) => (
        <p key={doc.type}>
          <Link to={doc.url}>{doc.title}</Link>
        </p>
      ))}
      {accept.isError && (
        <p role="alert">Не удалось сохранить выбор. Обновите документы и повторите.</p>
      )}
      <Button disabled={accept.isPending} onClick={() => accept.mutate()}>
        Принимаю условия и политику приватности
      </Button>
      <p>
        <Link to="/settings/account">Не принимать — управление аккаунтом и удаление</Link>
      </p>
      <p>
        <Link to="/support">Связаться с поддержкой</Link>
      </p>
    </Screen>
  );
}
export function LegalStatusBoundary({ children }: { children: React.ReactNode }) {
  const userId = useSession((state) => state.user?.id);
  const status = useQuery({
    queryKey: ['legal-status', userId],
    queryFn: () => api<LegalManifest & { required: boolean }>('/me/legal-status'),
    staleTime: 0,
  });
  if (status.isLoading)
    return (
      <Screen title="Проверяем редакцию документов">
        <AsyncState kind="loading" />
      </Screen>
    );
  if (!status.data || status.isError)
    return (
      <Screen title="Документы временно недоступны">
        <p>Продолжение заблокировано до проверки документов.</p>
        <Button
          onClick={() => {
            void status.refetch();
          }}
        >
          Повторить
        </Button>
        <Link to="/settings/account">Управление аккаунтом</Link>
      </Screen>
    );
  return status.data.required ? <LegalRenewal manifest={status.data} /> : <>{children}</>;
}

export function LegalDocumentBody({ body }: { body: string }) {
  return (
    <>
      {body.split('\n\n').map((paragraph, index) => {
        if (paragraph.startsWith('## ')) return <h2 key={index}>{paragraph.slice(3)}</h2>;
        if (paragraph.startsWith('# ')) return <h2 key={index}>{paragraph.slice(2)}</h2>;
        return (
          <p key={index} style={{ whiteSpace: 'pre-wrap' }}>
            {paragraph}
          </p>
        );
      })}
    </>
  );
}
